<template>
  <tippy
    v-if="appointmentData"
    theme="calendar-popover-theme"
    placement="right"
  >
    <template #default>
      <div
        class="flex gap-1 items-center style-appointment w-full overflow-hidden"
        :class="appointmentData.status"
      >
        <div
          class="w-2 h-3 rounded-full month-view-dot shrink-0"
          :style="`background-color: var(--${themeColors.statusColor})`"
        ></div>
        <div>{{ dayjs(appointmentData.time).format('H:mm') }}</div>
        <div>{{ appointmentData.customerName }}</div>
      </div>
    </template>

    <template #content>
      <div
        class="max-w-[380px] popover-container p-0 m-0"
        :class="'bg-' + themeColors.outcomeColor"
      >
        <div class="bg-primary p-4 text-primary rounded-lg -mt-2">
          <div class="flex justify-between gap-8 items-center mb-3">
            <div class="flex gap-2 items-center">
              <Avatar
                :icon="$t(getDictionaryItem('appointmentMethod', appointmentData.method)?.icon)"
                size="sm"
              />
              <div
                class="text-lg font-semibold"
                :class="'text-' + themeColors.textColor"
              >
                {{ $t(getDictionaryItem('appointmentStatus', appointmentData.status)?.localeKey) }}
              </div>
            </div>
            <div>
              <Chip
                icon="user"
                :rounded="false"
                size="sm"
              >
                {{ appointmentData.assignedToName || 'Unassigned' }}
              </Chip>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1">
              <div class="flex gap-2 items-center">
                <Icon type="location-dot" />
                <div>{{ appointmentData.dealershipName }}</div>
              </div>
              <div class="flex gap-2 items-center">
                <Icon :type="appointmentData.customerConfirmed ? 'user-check' : 'user-xmark'" />
                <div>
                  {{ appointmentData.customerConfirmed ? 'Customer confirmed' : 'Not confirmed' }}
                </div>
              </div>
            </div>

            <Separator v-if="appointmentData.note" />

            <div class="italic text-sm">{{ _truncate(appointmentData.note, { length: 160 }) }}</div>
          </div>
        </div>

        <div
          v-if="appointmentData.outcome || appointmentData.outcomeReason"
          class="p-4 rounded-lg"
        >
          <div class="flex flex-col gap-2">
            <div
              v-if="appointmentData.outcome"
              class="text-lg font-semibold"
            >
              {{ $t(getDictionaryItem('appointmentOutcome', appointmentData.outcome)?.localeKey) }}
            </div>
            <div
              v-if="appointmentData.outcomeReason"
              class="italic text-sm"
            >
              {{ _truncate(appointmentData.outcomeReason, { length: 130 }) }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </tippy>
</template>

<script setup>
import { computed } from 'vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Chip from '@/components/chip/Chip.vue'
import Separator from '@/components/separator/Separator.vue'
import Icon from '@/components/icon/Icon.vue'

import { getDictionaryItem } from '@/dictionary'
import _truncate from 'lodash/truncate'

const props = defineProps({
  appointmentData: {
    type: Object,
    default: null
  }
})

const themeColors = computed(() => {
  return colorOptions.find(theme => theme.name == props.appointmentData.status)
})

const colorOptions = [
  {
    name: 'unassigned',
    statusColor: 'utility-gray-600',
    textColor: 'gray-600',
    outcomeColor: 'gray-800'
  },
  {
    name: 'assigned',
    statusColor: 'utility-blue-900',
    textColor: 'blue-600',
    outcomeColor: 'blue-800'
  },
  {
    name: 'cancelled',
    statusColor: 'utility-error-600',
    textColor: 'red-600',
    outcomeColor: 'red-800'
  },
  {
    name: 'no-show',
    statusColor: 'utility-error-600',
    textColor: 'red-600',
    outcomeColor: 'red-800'
  },
  {
    name: 'completed',
    statusColor: 'utility-success-600',
    textColor: 'green-700',
    outcomeColor: 'green-800'
  },
  {
    name: 'ongoing',
    statusColor: 'utility-purple-600',
    textColor: 'purple-600',
    outcomeColor: 'purple-900'
  }
]
</script>

<style>
.tippy-box[data-theme~='calendar-popover-theme'] {
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.15);
  border-radius: calc(var(--rounded-lg) + 0.1rem);
}

.tippy-box[data-theme~='calendar-popover-theme'] .popover-container {
  border-radius: calc(var(--rounded-lg) + 0.1rem);
}

.tippy-box[data-theme~='calendar-popover-theme'] .tippy-content {
  padding: 0 !important;
}

.tippy-box[data-theme~='calendar-popover-theme'] .tippy-arrow {
  display: none !important;
}

.tippy-box[data-theme~='calendar-popover-theme'] .tippy-arrow::before {
  background-color: var(--arrow-color) !important;
}
</style>
